import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useCreateOperateurMutation } from 'actions/operateurs/mutations';
import GeneriqueModal from 'components/GeneriqueModal';
import ButtonCancel from 'components/buttons/ButtonCancel';
import ButtonGreen from 'components/buttons/ButtonGreen';
import './comptePaiement.css';

const ModalAjouterOperateur = ({ isOpen, onClose, type, dossier_id, onSuccess }) => {
  const [createOperateur, { isLoading }] = useCreateOperateurMutation();
  const { register, handleSubmit, formState: { errors }, reset } = useForm();
  const [selectedFile, setSelectedFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoError, setLogoError] = useState(null);  // ✅ State pour erreur logo

  const typeLabels = {
    'banque': 'banque',
    'monnaie_electronique': 'opérateur de paiement mobile',
    'caisse': 'caisse'
  };

  const typeLabel = typeLabels[type] || 'compte';

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setLogoError(null);  // ✅ Réinitialiser l'erreur
    
    if (file) {
      // Vérifier la taille (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        const errorMsg = 'Le fichier est trop volumineux (max 2MB)';
        setLogoError(errorMsg);
        toast.error(errorMsg);
        e.target.value = '';
        return;
      }
      
      // ✅ Vérifier le type - SEULEMENT PNG, JPEG, JPG, WebP (pas SVG!)
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        const errorMsg = 'Format non supporté. Utilisez PNG, JPEG ou WebP uniquement';
        setLogoError(errorMsg);
        toast.error(errorMsg);
        e.target.value = '';
        return;
      }
      
      // Sauvegarder le fichier
      setSelectedFile(file);
      
      // Créer la prévisualisation
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setLogoPreview(null);
    // Reset l'input file
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) fileInput.value = '';
  };

  const onSubmit = async (data) => {
    // Créer un FormData pour envoyer le fichier
    const formData = new FormData();
    formData.append('nom', data.nom);
    formData.append('type', type);
    formData.append('couleur', data.couleur || '#068f7a');
    formData.append('dossier_id', dossier_id);
    formData.append('is_active', 'true');
    formData.append('is_system', 'false');
    
    // Ajouter le fichier logo si sélectionné
    if (selectedFile) {
      console.log('📎 Debug Upload - Type de selectedFile:', selectedFile.constructor.name);
      console.log('📎 Debug Upload - Taille:', selectedFile.size, 'bytes');
      console.log('📎 Debug Upload - Type MIME:', selectedFile.type);
      console.log('📎 Debug Upload - Nom:', selectedFile.name);
      formData.append('logo', selectedFile, selectedFile.name);
    }
    
    // Debug: Afficher le contenu du FormData
    console.log('📦 Contenu du FormData:');
    for (let pair of formData.entries()) {
      console.log('  -', pair[0] + ':', pair[1]);
    }
    
    try {
      // Envoyer avec FormData
      const newOperateur = await createOperateur({ formData }).unwrap();
      
      toast.success(`${data.nom} ajouté avec succès !`);
      reset();
      setSelectedFile(null);
      setLogoPreview(null);
      setLogoError(null);
      
      // ✅ Appeler onSuccess AVANT de fermer le modal pour rafraîchir la liste
      if (onSuccess) {
        onSuccess(newOperateur);
      }
      
      // ✅ Fermer le modal SEULEMENT après succès
      onClose();
    } catch (error) {
      console.error('❌ Erreur upload:', error);
      
      // ✅ Extraire l'erreur du logo si elle existe
      if (error?.data?.message?.logo) {
        const logoErrorMsg = Array.isArray(error.data.message.logo) 
          ? error.data.message.logo[0] 
          : error.data.message.logo;
        setLogoError(logoErrorMsg);
        toast.error(`Erreur logo : ${logoErrorMsg}`);
      } 
      // ✅ Gérer l'erreur de doublon de nom
      else if (error?.data?.message?.nom) {
        const nomErrorMsg = Array.isArray(error.data.message.nom) 
          ? error.data.message.nom[0] 
          : error.data.message.nom;
        toast.error(`Erreur : ${nomErrorMsg}`);
      } 
      else {
        const errorMsg = error?.data?.message || error?.data?.error || "Erreur lors de l'ajout de l'opérateur";
        toast.error(errorMsg);
      }
      
      // ✅ NE PAS fermer le modal en cas d'erreur
    }
  };

  const handleClose = () => {
    reset();
    setSelectedFile(null);
    setLogoPreview(null);
    setLogoError(null);  // ✅ Réinitialiser l'erreur
    onClose();
  };

  return (
    <GeneriqueModal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Ajouter une ${typeLabel}`}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="modal-form-responsive" style={{ display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'stretch', width: '100%' }}>
        
        {/* Nom de l'opérateur */}
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label">
            Nom de la {typeLabel} <span style={{ color: 'red' }}>*</span>
          </label>
          <input
            type="text"
            className="form-control"
            style={{ width: '100%' }}
            placeholder={
              type === 'banque' 
                ? 'Ex: Ecobank Dakar, SGBS Plateau' 
                : type === 'monnaie_electronique'
                ? 'Ex: Free Money, My Money'
                : 'Ex: Caisse Principale'
            }
            {...register('nom', { 
              required: `Le nom de la ${typeLabel} est obligatoire`,
              minLength: {
                value: 2,
                message: 'Le nom doit contenir au moins 2 caractères'
              }
            })}
          />
          {errors.nom && <span className="error-message" style={{ color: 'red', fontSize: 12 }}>{errors.nom.message}</span>}
        </div>

         {/* Logo - Upload de fichier */}
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label">Logo (optionnel)</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
            {/* Input File avec style personnalisé */}
            <div style={{ position: 'relative', width: '100%' }}>
              <input
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                onChange={handleFileChange}
                style={{
                  width: '100%',
                  padding: '4px',
                  border: logoError ? '2px dashed #EF4444' : '2px dashed #D1D5DB',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  backgroundColor: logoError ? '#FEE2E2' : '#F9FAFB',
                  transition: 'all 0.2s',
                }}
                onMouseOver={(e) => {
                  if (!logoError) {
                    e.target.style.borderColor = '#068f7a';
                    e.target.style.backgroundColor = '#EFFAF7';
                  }
                }}
                onMouseOut={(e) => {
                  if (!logoError) {
                    e.target.style.borderColor = '#D1D5DB';
                    e.target.style.backgroundColor = '#F9FAFB';
                  }
                }}
              />
            </div>
            
            {/* Afficher l'erreur du logo */}
            {logoError && (
              <div style={{
                padding: '8px 12px',
                borderRadius: 6,
                backgroundColor: '#FEE2E2',
                border: '1px solid #EF4444',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}>
                <span style={{ fontSize: 16, color: '#991B1B' }}>✕</span>
                <span style={{ fontSize: 12, color: '#991B1B', fontWeight: 500 }}>
                  {logoError}
                </span>
              </div>
            )}
            
            {/* Prévisualisation du logo */}
            {logoPreview && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: 12,
                borderRadius: 8,
                backgroundColor: '#F9FAFB',
                border: '1px solid #E5E7EB'
              }}>
                <img
                  src={logoPreview}
                  alt="Aperçu logo"
                  style={{
                    width: 60,
                    height: 60,
                    objectFit: 'contain',
                    borderRadius: 6,
                    border: '1px solid #E5E7EB',
                    backgroundColor: '#fff',
                    padding: 4
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                    Aperçu du logo
                  </div>
                  <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>
                    {selectedFile?.name} ({(selectedFile?.size / 1024).toFixed(0)} KB)
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveFile}
                  style={{
                    padding: '6px 12px',
                    fontSize: 11,
                    fontWeight: 600,
                    color: '#EF4444',
                    background: 'none',
                    border: '1px solid #EF4444',
                    borderRadius: 5,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#FEE2E2';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  ✕ Retirer
                </button>
              </div>
            )}
            
            <span style={{ fontSize: 11, color: '#9CA3AF' }}>
              Formats acceptés : PNG, JPEG, WebP uniquement (max 2MB)
            </span>
          </div>
        </div>

        {/* Couleur */}
        {/* <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label">Couleur (optionnel)</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="color"
              {...register('couleur')}
              defaultValue="#068f7a"
              style={{
                width: 50,
                height: 38,
                border: '1px solid #ddd',
                borderRadius: 5,
                cursor: 'pointer'
              }}
            />
            <span style={{ fontSize: 12, color: '#6B7280' }}>
              Utilisée pour l&apos;affichage dans l&apos;interface
            </span>
          </div>
        </div> */}

        {/* Info */}
        <div style={{
          padding: 12,
          borderRadius: 8,
          backgroundColor: '#f7dfdf',
          border: '1px solid #ef4444ab',
          fontSize: 12,
          color: '#EF4444',
          width: '100%'
        }}>
          <strong>Info :</strong> Cette {typeLabel} sera privée à votre dossier.
          {type === 'banque' && ' Elle apparaîtra dans vos moyens de paiement bancaires.'}
          {type === 'monnaie_electronique' && ' Elle apparaîtra dans vos moyens de paiement mobiles.'}
        </div>

        {/* Footer */}
        <div className="modal-footer" style={{ marginTop: 12, display: 'flex', gap: 10, justifyContent: 'flex-end', width: '100%' }}>
          <ButtonCancel text="Annuler" onClick={handleClose} disabled={isLoading} />
          <ButtonGreen
            text={isLoading ? 'Ajout en cours...' : 'Ajouter'}
            type="submit"
            disabled={isLoading}
            style={{
              opacity: isLoading ? 0.6 : 1,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            {isLoading && (
              <span style={{
                display: 'inline-block',
                width: 14,
                height: 14,
                border: '2px solid #fff',
                borderTopColor: 'transparent',
                borderRadius: '50%',
                animation: 'spin 0.6s linear infinite'
              }} />
            )}
            {isLoading ? 'Ajout en cours...' : 'Ajouter'}
          </ButtonGreen>
        </div>
        
        {/* Ajouter l'animation de spin */}
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </form>
    </GeneriqueModal>
  );
};

export default ModalAjouterOperateur;
